import { User, UserRole, Recipe, InventoryItem, MeasurementUnit, Category, TrainingProgram, RoutineDefinition, RoutineAssignment, Shift, TimeOffRequest, EmployeeAvailability, ScheduleGroup, BusinessProfile, Tenant, BusinessRequest, StockReport, Supply, OrderList } from '../types';
import { api } from './apiService';

export const DEMO_TENANT_ID = 'demo_store_1'; // Dedicated Demo ID
const PLATFORM_ID = 'platform';

// Demo seed data declared early to avoid temporal dead zone issues
const INITIAL_UNITS_DEMO: MeasurementUnit[] = [
    { id: 'u1', tenantId: DEMO_TENANT_ID, name: 'Gram', abbreviation: 'g' },
    { id: 'u2', tenantId: DEMO_TENANT_ID, name: 'Ounce', abbreviation: 'oz' },
    { id: 'u3', tenantId: DEMO_TENANT_ID, name: 'Milliliter', abbreviation: 'ml' },
    { id: 'u4', tenantId: DEMO_TENANT_ID, name: 'Piece', abbreviation: 'pc' },
    { id: 'u5', tenantId: DEMO_TENANT_ID, name: 'Shot', abbreviation: 'shot' }
];
const INITIAL_CATEGORIES_DEMO: Category[] = [
    { id: 'c1', tenantId: DEMO_TENANT_ID, name: 'Coffee' },
    { id: 'c2', tenantId: DEMO_TENANT_ID, name: 'Tea' },
    { id: 'c3', tenantId: DEMO_TENANT_ID, name: 'Bakery' }
];
const INITIAL_INVENTORY_DEMO: InventoryItem[] = [
    { id: 'i1', tenantId: DEMO_TENANT_ID, name: 'Espresso Beans', unitId: 'u1', costPerUnit: 0.02, stockQuantity: 5000 },
    { id: 'i2', tenantId: DEMO_TENANT_ID, name: 'Milk', unitId: 'u3', costPerUnit: 0.002, stockQuantity: 60000 },
    { id: 'i3', tenantId: DEMO_TENANT_ID, name: 'Vanilla Syrup', unitId: 'u3', costPerUnit: 0.004, stockQuantity: 15000 },
    { id: 'i4', tenantId: DEMO_TENANT_ID, name: 'Chai Concentrate', unitId: 'u3', costPerUnit: 0.006, stockQuantity: 12000 },
    { id: 'i5', tenantId: DEMO_TENANT_ID, name: 'Croissant Dough', unitId: 'u4', costPerUnit: 0.8, stockQuantity: 60 },
    { id: 'i6', tenantId: DEMO_TENANT_ID, name: 'Dark Chocolate Sauce', unitId: 'u3', costPerUnit: 0.005, stockQuantity: 8000 },
    { id: 'i7', tenantId: DEMO_TENANT_ID, name: 'Cold Brew Concentrate', unitId: 'u3', costPerUnit: 0.007, stockQuantity: 10000 },
    { id: 'i8', tenantId: DEMO_TENANT_ID, name: 'Whipped Cream', unitId: 'u4', costPerUnit: 0.15, stockQuantity: 200 },
    { id: 'i9', tenantId: DEMO_TENANT_ID, name: 'Oat Milk', unitId: 'u3', costPerUnit: 0.0035, stockQuantity: 42000 },
    { id: 'i10', tenantId: DEMO_TENANT_ID, name: 'Cinnamon Powder', unitId: 'u1', costPerUnit: 0.03, stockQuantity: 500 }
];

// --- INITIAL DATA (DEMO ONLY) ---
const INITIAL_TENANTS: Tenant[] = [
    {
        id: DEMO_TENANT_ID,
        name: "Live Demo Coffee Co.",
        ownerEmail: "demo@trycafemaster.com",
        subscriptionStatus: 'active',
        subscriptionCost: 0,
        createdAt: Date.now()
    }
];

const INITIAL_REQUESTS: BusinessRequest[] = [];

// Clean initial data - ONLY Demo items remain
const INITIAL_UNITS: MeasurementUnit[] = INITIAL_UNITS_DEMO;

const INITIAL_CATEGORIES: Category[] = INITIAL_CATEGORIES_DEMO;

const INITIAL_INVENTORY: InventoryItem[] = INITIAL_INVENTORY_DEMO;

const INITIAL_SUPPLIES: Supply[] = [
    { id: 'sup1', tenantId: DEMO_TENANT_ID, name: 'Paper Cup', sizes: [{ id: 's1', name: 'Small (8oz)', cost: 0.12 }, { id: 's2', name: 'Medium (12oz)', cost: 0.15 }, { id: 's3', name: 'Large (16oz)', cost: 0.18 }], createdAt: Date.now() },
    { id: 'sup2', tenantId: DEMO_TENANT_ID, name: 'Plastic Lid', sizes: [{ id: 'l1', name: 'Small', cost: 0.05 }, { id: 'l2', name: 'Medium/Large', cost: 0.06 }], createdAt: Date.now() },
    { id: 'sup3', tenantId: DEMO_TENANT_ID, name: 'Napkins', sizes: [{ id: 'n1', name: 'Pack of 2', cost: 0.02 }], createdAt: Date.now() }
];

const INITIAL_STOCK_REPORTS: StockReport[] = [
    {
        reportId: 'report_demo1',
        tenantId: DEMO_TENANT_ID,
        itemId: 'i2',
        itemType: 'ingredient',
        itemName: 'Milk',
        userId: 'barista_alex',
        userName: 'Alex Rivera',
        userEmail: 'alex@demo-coffee.com',
        message: 'Running low on milk, might run out by end of shift',
        timestamp: Date.now() - 3600000,
        status: 'pending',
        reportType: 'low_stock',
        reportedQuantity: 5,
        systemQuantity: 20,
        unitAbbreviation: 'gal'
    },
    {
        reportId: 'report_demo2',
        tenantId: DEMO_TENANT_ID,
        itemId: 'sup1',
        itemType: 'supply',
        itemName: 'Paper Cup',
        userId: 'barista_jamie',
        userName: 'Jamie Chen',
        userEmail: 'jamie@demo-coffee.com',
        message: 'Large cups completely out!',
        timestamp: Date.now() - 7200000,
        status: 'approved',
        reportType: 'out_of_stock',
        reportedQuantity: 0,
        systemQuantity: 50,
        unitAbbreviation: 'pcs',
        adminResponse: 'Inventory updated. New order placed.',
        adminRespondedBy: 'Demo Manager',
        adminRespondedAt: Date.now() - 6000000,
        appliedQuantity: 0
    },
    {
        reportId: 'report_demo3',
        tenantId: DEMO_TENANT_ID,
        itemId: 'i1',
        itemType: 'ingredient',
        itemName: 'Coffee Beans',
        userId: 'barista_alex',
        userName: 'Alex Rivera',
        userEmail: 'alex@demo-coffee.com',
        message: 'Counted bags this morning - we have more than system shows',
        timestamp: Date.now() - 1800000,
        status: 'pending',
        reportType: 'quantity_correction',
        reportedQuantity: 12,
        systemQuantity: 8,
        unitAbbreviation: 'bags'
    }
];

const INITIAL_ORDER_LISTS: OrderList[] = [
    {
        id: 'orderlist_demo1',
        tenantId: DEMO_TENANT_ID,
        name: 'Walmart Shopping',
        color: 'blue',
        items: [
            { id: 'item_1', itemId: 'i2', itemType: 'ingredient', itemName: 'Milk', quantity: 10, unitAbbreviation: 'gal', note: 'Get whole milk', addedAt: Date.now() - 86400000, addedBy: 'Demo Manager', isChecked: false },
            { id: 'item_2', itemId: 'sup3', itemType: 'supply', itemName: 'Napkins', quantity: 5, unitAbbreviation: 'packs', addedAt: Date.now() - 43200000, addedBy: 'Demo Manager', isChecked: true }
        ],
        createdAt: Date.now() - 172800000,
        createdBy: 'Demo Manager',
        order: 0
    },
    {
        id: 'orderlist_demo2',
        tenantId: DEMO_TENANT_ID,
        name: 'Coffee Supplier Order',
        color: 'amber',
        items: [
            { id: 'item_3', itemId: 'i1', itemType: 'ingredient', itemName: 'Coffee Beans', quantity: 20, unitAbbreviation: 'bags', note: 'Order from usual supplier', addedAt: Date.now() - 3600000, addedBy: 'Demo Manager', isChecked: false }
        ],
        createdAt: Date.now() - 86400000,
        createdBy: 'Demo Manager',
        order: 1
    }
];

// Only Demo User and Super Admin Placeholder (Authentication handled by App.tsx)
const INITIAL_USERS: User[] = [
    {
        id: 'demo_user',
        tenantId: DEMO_TENANT_ID,
        name: 'Demo Manager',
        email: 'demo@trycafemaster.com',
        role: UserRole.ADMIN,
        status: 'active',
        password: 'demo',
        completedTrainingRecipeIds: []
    },
    {
        id: 'barista_alex',
        tenantId: DEMO_TENANT_ID,
        name: 'Alex Rivera',
        email: 'alex@demo-coffee.com',
        role: UserRole.EMPLOYEE,
        status: 'active',
        completedTrainingRecipeIds: ['demo_rec1']
    },
    {
        id: 'barista_jamie',
        tenantId: DEMO_TENANT_ID,
        name: 'Jamie Chen',
        email: 'jamie@demo-coffee.com',
        role: UserRole.EMPLOYEE,
        status: 'active',
        completedTrainingRecipeIds: []
    },
    {
        id: 'baker_sam',
        tenantId: DEMO_TENANT_ID,
        name: 'Sam Taylor',
        email: 'sam@demo-coffee.com',
        role: UserRole.EMPLOYEE,
        status: 'active',
        completedTrainingRecipeIds: []
    }
];

const INITIAL_RECIPES: Recipe[] = [
  { 
      id: 'demo_rec1', 
      tenantId: DEMO_TENANT_ID, 
      title: 'Caramel Macchiato', 
      description: 'Espresso stained with milk foam and topped with caramel drizzle.', 
      categoryId: 'c1', 
      createdBy: 'demo_user', 
            createdAt: Date.now(),
            imageUrl: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=1200&q=80', 
      price: 5.25,
      sizes: [{id:'r', name: 'Regular'}, {id:'l', name: 'Large'}], 
      steps: [
          {id: 'ds1', order: 0, description: 'Steam milk to 150F', ingredients: []},
          {id: 'ds2', order: 1, description: 'Add 20ml vanilla syrup', ingredients: [{ id: 'ing1', inventoryItemId: 'i3', quantityPerSize: { r: 20, l: 30 } }]},
          {id: 'ds3', order: 2, description: 'Mark with espresso shot', ingredients: [{ id: 'ing2', inventoryItemId: 'i1', quantityPerSize: { r: 18, l: 36 } }]},
          {id: 'ds4', order: 3, description: 'Top with caramel crosshatch', ingredients: []}
      ] 
  },
  { 
      id: 'demo_rec2', 
      tenantId: DEMO_TENANT_ID, 
      title: 'Iced Chai Latte', 
      description: 'Spiced chai over ice with creamy milk finish.', 
      categoryId: 'c2', 
      createdBy: 'demo_user', 
      createdAt: Date.now(),
      imageUrl: 'https://images.unsplash.com/photo-1509042239860-f550ce710b93?auto=format&fit=crop&w=1200&q=80', 
    price: 4.95,
      sizes: [{id:'r', name: 'Regular'}, {id:'l', name: 'Large'}], 
      steps: [
          {id: 'ds5', order: 0, description: 'Fill cup with ice', ingredients: []},
          {id: 'ds6', order: 1, description: 'Add chai concentrate', ingredients: [{ id: 'ing3', inventoryItemId: 'i4', quantityPerSize: { r: 60, l: 90 } }]},
          {id: 'ds7', order: 2, description: 'Top with milk', ingredients: [{ id: 'ing4', inventoryItemId: 'i2', quantityPerSize: { r: 180, l: 240 } }]}
      ]
  },
  { 
      id: 'demo_rec3', 
      tenantId: DEMO_TENANT_ID, 
      title: 'Butter Croissant', 
      description: 'Flaky, buttery pastry baked fresh each morning.', 
      categoryId: 'c3', 
      createdBy: 'demo_user', 
      createdAt: Date.now(),
      imageUrl: 'https://images.unsplash.com/photo-1509440159596-0249088772ff?auto=format&fit=crop&w=1200&q=80', 
    price: 3.95,
      sizes: [{id:'std', name: 'Standard'}], 
      steps: [
          {id: 'ds8', order: 0, description: 'Thaw dough overnight', ingredients: [{ id: 'ing5', inventoryItemId: 'i5', quantityPerSize: { std: 1 } }]},
          {id: 'ds9', order: 1, description: 'Egg wash and proof', ingredients: []},
          {id: 'ds10', order: 2, description: 'Bake at 375F for 18 minutes', ingredients: []}
      ]
  },
  {
      id: 'demo_rec4',
      tenantId: DEMO_TENANT_ID,
      title: 'Iced Oat Caramel Latte',
      description: 'Smooth oat milk latte over ice with caramel finish.',
      categoryId: 'c1',
      createdBy: 'demo_user',
      createdAt: Date.now(),
      imageUrl: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=1200&q=80',
    price: 5.75,
      sizes: [{ id: 'r', name: 'Regular' }, { id: 'l', name: 'Large' }],
      steps: [
          { id: 'ds11', order: 0, description: 'Fill cup with ice', ingredients: [] },
          { id: 'ds12', order: 1, description: 'Add caramel drizzle to cup', ingredients: [] },
          { id: 'ds13', order: 2, description: 'Pour cold brew concentrate', ingredients: [{ id: 'ing6', inventoryItemId: 'i7', quantityPerSize: { r: 120, l: 160 } }] },
          { id: 'ds14', order: 3, description: 'Top with oat milk', ingredients: [{ id: 'ing7', inventoryItemId: 'i9', quantityPerSize: { r: 140, l: 200 } }] }
      ]
  },
  {
      id: 'demo_rec5',
      tenantId: DEMO_TENANT_ID,
      title: 'Mocha with Whip',
      description: 'Chocolatey espresso with velvety milk and whipped cream.',
      categoryId: 'c1',
      createdBy: 'demo_user',
      createdAt: Date.now(),
      imageUrl: 'https://images.unsplash.com/photo-1509042239860-f550ce710b93?auto=format&fit=crop&w=1200&q=80',
    price: 5.5,
      sizes: [{ id: 'r', name: 'Regular' }, { id: 'l', name: 'Large' }],
      steps: [
          { id: 'ds15', order: 0, description: 'Blend chocolate sauce with espresso', ingredients: [{ id: 'ing8', inventoryItemId: 'i6', quantityPerSize: { r: 25, l: 35 } }, { id: 'ing9', inventoryItemId: 'i1', quantityPerSize: { r: 18, l: 36 } }] },
          { id: 'ds16', order: 1, description: 'Steam milk to 150F', ingredients: [{ id: 'ing10', inventoryItemId: 'i2', quantityPerSize: { r: 180, l: 240 } }] },
          { id: 'ds17', order: 2, description: 'Top with whipped cream and cocoa dust', ingredients: [{ id: 'ing11', inventoryItemId: 'i8', quantityPerSize: { r: 1, l: 1 } }, { id: 'ing12', inventoryItemId: 'i10', quantityPerSize: { r: 1, l: 1 } }] }
      ]
  }
];

const INITIAL_ROUTINES: RoutineDefinition[] = [
    {
        id: 'rd1',
        tenantId: DEMO_TENANT_ID,
        name: 'Open Cafe',
        tasks: [
            { id: 'rt1', time: '06:30', type: 'general', description: 'Dial in espresso grinder' },
            { id: 'rt2', time: '06:45', type: 'recipe', description: 'Pull test shot', recipeId: 'demo_rec1' }
        ]
    }
];
const INITIAL_ASSIGNMENTS: RoutineAssignment[] = [
    {
        id: 'ra1',
        tenantId: DEMO_TENANT_ID,
        employeeId: 'barista_alex',
        routineDefinitionId: 'rd1',
        date: new Date().toISOString().split('T')[0],
        completedTaskIds: []
    }
];
const INITIAL_SHIFTS: Shift[] = [
    {
        id: 'shift1',
        tenantId: DEMO_TENANT_ID,
        employeeId: 'barista_alex',
        start: new Date().toISOString(),
        end: new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString()
    },
    {
        id: 'shift2',
        tenantId: DEMO_TENANT_ID,
        employeeId: 'barista_jamie',
        start: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
        end: new Date(Date.now() + 6 * 60 * 60 * 1000).toISOString()
    },
    {
        id: 'shift3',
        tenantId: DEMO_TENANT_ID,
        employeeId: 'baker_sam',
        start: new Date().toISOString(),
        end: new Date(Date.now() + 5 * 60 * 60 * 1000).toISOString()
    }
];
const INITIAL_REQUESTS_OFF: TimeOffRequest[] = [
    {
        id: 'req1',
        tenantId: DEMO_TENANT_ID,
        employeeId: 'barista_jamie',
        startDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        reason: 'Family event',
        status: 'pending'
    },
    {
        id: 'req2',
        tenantId: DEMO_TENANT_ID,
        employeeId: 'barista_alex',
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        reason: 'Doctor appointment',
        status: 'approved'
    }
];
const INITIAL_AVAILABILITY: EmployeeAvailability[] = [
    { employeeId: 'barista_alex', tenantId: DEMO_TENANT_ID, unavailableDays: [0] },
    { employeeId: 'barista_jamie', tenantId: DEMO_TENANT_ID, unavailableDays: [6] },
    { employeeId: 'baker_sam', tenantId: DEMO_TENANT_ID, unavailableDays: [] }
];
const INITIAL_SCHEDULE_GROUPS: ScheduleGroup[] = [
    {
        id: 'group1',
        tenantId: DEMO_TENANT_ID,
        name: 'Demo Week',
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(Date.now() + 4 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        status: 'published',
        publishedAt: Date.now() - 86400000,
        publishedBy: 'demo_user'
    },
    {
        id: 'group2',
        tenantId: DEMO_TENANT_ID,
        name: 'Latte Art Throwdown',
        startDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        status: 'draft'
    }
];
const INITIAL_TRAINING: TrainingProgram[] = [
    {
        id: 'tp1',
        tenantId: DEMO_TENANT_ID,
        employeeId: 'barista_jamie',
        title: 'Milk Steaming Basics',
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        recipeIds: ['demo_rec1'],
        assignedBy: 'demo_user',
        status: 'active'
    },
    {
        id: 'tp2',
        tenantId: DEMO_TENANT_ID,
        employeeId: 'baker_sam',
        title: 'Morning Bakery Run',
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        recipeIds: ['demo_rec3'],
        assignedBy: 'demo_user',
        status: 'pending'
    }
];

// This is now a map: tenantId -> Profile
const INITIAL_BUSINESS_PROFILES: Record<string, BusinessProfile> = {
    [PLATFORM_ID]: {
        name: 'Cafe Menu Master Platform',
        address: '',
        phone: '',
        email: 'icofeemart@gmail.com',
        taxId: '',
        themeColor: 'midnight',
        notificationEmails: ['icofeemart@gmail.com']
    },
    [DEMO_TENANT_ID]: {
        name: 'Live Demo Coffee Co.',
        address: '101 Internet Blvd',
        phone: '(800) 555-DEMO',
        email: 'demo@trycafemaster.com',
        taxId: '',
        themeColor: 'mocha',
        ownerName: 'Demo Admin',
        ownerEmail: 'demo@trycafemaster.com'
    }
};

// LocalStorage Helpers
const get = <T,>(key: string, initial: T): T => {
  const stored = localStorage.getItem(key);
  if (!stored) {
    localStorage.setItem(key, JSON.stringify(initial));
    return initial;
  }
  return JSON.parse(stored);
};

const set = <T,>(key: string, data: T): void => {
  localStorage.setItem(key, JSON.stringify(data));
};

export const db = {
  businessRequests: {
      getAll: async () => {
          const data = await api.leads.getAll();
          return Array.isArray(data) ? data : [];
      },
      add: async (req: BusinessRequest) => {
          if (!req.contactName || !req.email || !req.businessName) {
              throw new Error('Missing required fields: contactName, email, businessName');
          }
          return await api.leads.create(req);
      },
      update: async (req: BusinessRequest) => await api.leads.update(req.id, req),
  },
  tenants: {
      getAll: () => get<Tenant[]>('tenants', INITIAL_TENANTS),
      add: (tenant: Tenant) => {
          const items = get<Tenant[]>('tenants', INITIAL_TENANTS);
          set('tenants', [...items, tenant]);
      },
      update: (tenant: Tenant) => {
          const items = get<Tenant[]>('tenants', INITIAL_TENANTS);
          set('tenants', items.map(t => t.id === tenant.id ? tenant : t));
      },
      // Mock payment history generator
      getPayments: (tenantId: string) => {
          const tenant = get<Tenant[]>('tenants', INITIAL_TENANTS).find(t => t.id === tenantId);
          if (!tenant) return [];
          
          const payments = [];
          const startDate = new Date(tenant.createdAt);
          const now = new Date();
          let currentDate = new Date(startDate);
          
          // Generate monthly payments from creation until now
          while(currentDate <= now) {
              payments.push({
                  id: 'pay_' + currentDate.getTime(),
                  date: currentDate.toISOString().split('T')[0],
                  amount: tenant.subscriptionCost,
                  status: 'paid'
              });
              currentDate.setMonth(currentDate.getMonth() + 1);
          }
          return payments.reverse();
      }
  },
  units: {
    getAll: (tenantId: string) => get<MeasurementUnit[]>('units', INITIAL_UNITS).filter(i => i.tenantId === tenantId),
    add: (item: MeasurementUnit) => {
      const items = get<MeasurementUnit[]>('units', INITIAL_UNITS);
      set('units', [...items, item]);
    },
    delete: (id: string) => {
        const items = get<MeasurementUnit[]>('units', INITIAL_UNITS);
        set('units', items.filter(i => i.id !== id));
    }
  },
  categories: {
    getAll: (tenantId: string) => get<Category[]>('categories', INITIAL_CATEGORIES).filter(i => i.tenantId === tenantId),
    add: (item: Category) => {
      const items = get<Category[]>('categories', INITIAL_CATEGORIES);
      set('categories', [...items, item]);
    },
    delete: (id: string) => {
        const items = get<Category[]>('categories', INITIAL_CATEGORIES);
        set('categories', items.filter(i => i.id !== id));
    }
  },
  inventory: {
    getAll: (tenantId: string) => get<InventoryItem[]>('inventory', INITIAL_INVENTORY).filter(i => i.tenantId === tenantId),
    add: (item: InventoryItem) => {
      const items = get<InventoryItem[]>('inventory', INITIAL_INVENTORY);
      set('inventory', [...items, item]);
    },
    update: (item: InventoryItem) => {
      const items = get<InventoryItem[]>('inventory', INITIAL_INVENTORY);
      set('inventory', items.map(i => i.id === item.id ? item : i));
    },
    delete: (id: string) => {
        const items = get<InventoryItem[]>('inventory', INITIAL_INVENTORY);
        set('inventory', items.filter(i => i.id !== id));
    }
  },
  supplies: {
    getAll: (tenantId: string) => get<Supply[]>('supplies', INITIAL_SUPPLIES).filter(i => i.tenantId === tenantId),
    add: (item: Supply) => {
      const items = get<Supply[]>('supplies', INITIAL_SUPPLIES);
      set('supplies', [...items, item]);
    },
    update: (item: Supply) => {
      const items = get<Supply[]>('supplies', INITIAL_SUPPLIES);
      set('supplies', items.map(i => i.id === item.id ? item : i));
    },
    delete: (id: string) => {
        const items = get<Supply[]>('supplies', INITIAL_SUPPLIES);
        set('supplies', items.filter(i => i.id !== id));
    }
  },
  stockReports: {
    getAll: (tenantId: string) => get<StockReport[]>('stockReports', INITIAL_STOCK_REPORTS).filter(i => i.tenantId === tenantId),
    add: (report: StockReport) => {
      const items = get<StockReport[]>('stockReports', INITIAL_STOCK_REPORTS);
      set('stockReports', [...items, report]);
    },
    update: (report: StockReport) => {
      const items = get<StockReport[]>('stockReports', INITIAL_STOCK_REPORTS);
      set('stockReports', items.map(i => i.reportId === report.reportId ? { ...i, ...report } : i));
    },
    delete: (reportId: string) => {
        const items = get<StockReport[]>('stockReports', INITIAL_STOCK_REPORTS);
        set('stockReports', items.filter(i => i.reportId !== reportId));
    }
  },
  orderLists: {
    getAll: (tenantId: string) => get<OrderList[]>('orderLists', INITIAL_ORDER_LISTS).filter(i => i.tenantId === tenantId),
    get: (listId: string) => get<OrderList[]>('orderLists', INITIAL_ORDER_LISTS).find(i => i.id === listId),
    add: (list: OrderList) => {
      const items = get<OrderList[]>('orderLists', INITIAL_ORDER_LISTS);
      set('orderLists', [...items, list]);
    },
    update: (list: OrderList) => {
      const items = get<OrderList[]>('orderLists', INITIAL_ORDER_LISTS);
      set('orderLists', items.map(i => i.id === list.id ? { ...i, ...list } : i));
    },
    delete: (listId: string) => {
      const items = get<OrderList[]>('orderLists', INITIAL_ORDER_LISTS);
      set('orderLists', items.filter(i => i.id !== listId));
    }
  },
  recipes: {
    getAll: (tenantId: string) => get<Recipe[]>('recipes', INITIAL_RECIPES).filter(i => i.tenantId === tenantId),
    add: (item: Recipe) => {
      const items = get<Recipe[]>('recipes', INITIAL_RECIPES);
      set('recipes', [...items, item]);
    },
    update: (item: Recipe) => {
      const items = get<Recipe[]>('recipes', INITIAL_RECIPES);
      set('recipes', items.map(i => i.id === item.id ? item : i));
    },
    delete: (id: string) => {
      const items = get<Recipe[]>('recipes', INITIAL_RECIPES);
      set('recipes', items.filter(i => i.id !== id));
    }
  },
  training: {
      getAll: (tenantId: string) => get<TrainingProgram[]>('training', INITIAL_TRAINING).filter(i => i.tenantId === tenantId),
      add: (program: TrainingProgram) => {
          const items = get<TrainingProgram[]>('training', INITIAL_TRAINING);
          set('training', [...items, program]);
      },
      update: (program: TrainingProgram) => {
          const items = get<TrainingProgram[]>('training', INITIAL_TRAINING);
          set('training', items.map(i => i.id === program.id ? program : i));
      },
      delete: (id: string) => {
          const items = get<TrainingProgram[]>('training', INITIAL_TRAINING);
          set('training', items.filter(i => i.id !== id));
      }
  },
  routines: {
      getDefinitions: (tenantId: string) => get<RoutineDefinition[]>('routineDefinitions', INITIAL_ROUTINES).filter(i => i.tenantId === tenantId),
      addDefinition: (def: RoutineDefinition) => {
          const items = get<RoutineDefinition[]>('routineDefinitions', INITIAL_ROUTINES);
          set('routineDefinitions', [...items, def]);
      },
      updateDefinition: (def: RoutineDefinition) => {
          const items = get<RoutineDefinition[]>('routineDefinitions', INITIAL_ROUTINES);
          set('routineDefinitions', items.map(i => i.id === def.id ? def : i));
      },
      deleteDefinition: (id: string) => {
          const items = get<RoutineDefinition[]>('routineDefinitions', INITIAL_ROUTINES);
          set('routineDefinitions', items.filter(i => i.id !== id));
      },
      getAssignments: (tenantId: string) => get<RoutineAssignment[]>('routineAssignments', INITIAL_ASSIGNMENTS).filter(i => i.tenantId === tenantId),
      addAssignment: (assign: RoutineAssignment) => {
          const items = get<RoutineAssignment[]>('routineAssignments', INITIAL_ASSIGNMENTS);
          set('routineAssignments', [...items, assign]);
      },
      updateAssignment: (assign: RoutineAssignment) => {
           const items = get<RoutineAssignment[]>('routineAssignments', INITIAL_ASSIGNMENTS);
           set('routineAssignments', items.map(i => i.id === assign.id ? assign : i));
      },
      deleteAssignment: (id: string) => {
          const items = get<RoutineAssignment[]>('routineAssignments', INITIAL_ASSIGNMENTS);
          set('routineAssignments', items.filter(i => i.id !== id));
      }
  },
  shifts: {
      getAll: (tenantId: string) => get<Shift[]>('shifts', INITIAL_SHIFTS).filter(i => i.tenantId === tenantId),
      add: (shift: Shift) => {
          const items = get<Shift[]>('shifts', INITIAL_SHIFTS);
          set('shifts', [...items, shift]);
      },
      delete: (id: string) => {
          const items = get<Shift[]>('shifts', INITIAL_SHIFTS);
          set('shifts', items.filter(i => i.id !== id));
      }
  },
  scheduleGroups: {
      getAll: (tenantId: string) => get<ScheduleGroup[]>('scheduleGroups', INITIAL_SCHEDULE_GROUPS).filter(i => i.tenantId === tenantId),
      add: (group: ScheduleGroup) => {
          const items = get<ScheduleGroup[]>('scheduleGroups', INITIAL_SCHEDULE_GROUPS);
          set('scheduleGroups', [...items, { ...group, status: group.status || 'draft' }]);
      },
      update: (group: ScheduleGroup) => {
          const items = get<ScheduleGroup[]>('scheduleGroups', INITIAL_SCHEDULE_GROUPS);
          set('scheduleGroups', items.map(i => i.id === group.id ? { ...i, ...group } : i));
      },
      delete: (id: string) => {
          const items = get<ScheduleGroup[]>('scheduleGroups', INITIAL_SCHEDULE_GROUPS);
          set('scheduleGroups', items.filter(i => i.id !== id));
      },
      publish: (id: string) => {
          const items = get<ScheduleGroup[]>('scheduleGroups', INITIAL_SCHEDULE_GROUPS);
          set('scheduleGroups', items.map(i => i.id === id ? { ...i, status: 'published' as const, publishedAt: Date.now(), publishedBy: 'demo_user' } : i));
      },
      unpublish: (id: string) => {
          const items = get<ScheduleGroup[]>('scheduleGroups', INITIAL_SCHEDULE_GROUPS);
          set('scheduleGroups', items.map(i => i.id === id ? { ...i, status: 'draft' as const, publishedAt: undefined, publishedBy: undefined } : i));
      }
  },
  requests: {
      getAll: (tenantId: string) => get<TimeOffRequest[]>('requests', INITIAL_REQUESTS_OFF).filter(i => i.tenantId === tenantId),
      add: (req: TimeOffRequest) => {
          const items = get<TimeOffRequest[]>('requests', INITIAL_REQUESTS_OFF);
          set('requests', [...items, req]);
      },
      update: (req: TimeOffRequest) => {
          const items = get<TimeOffRequest[]>('requests', INITIAL_REQUESTS_OFF);
          set('requests', items.map(i => i.id === req.id ? req : i));
      },
      delete: (id: string) => {
          const items = get<TimeOffRequest[]>('requests', INITIAL_REQUESTS_OFF);
          set('requests', items.filter(i => i.id !== id));
      }
  },
  availability: {
      getAll: (tenantId: string) => get<EmployeeAvailability[]>('availability', INITIAL_AVAILABILITY).filter(i => i.tenantId === tenantId),
      update: (avail: EmployeeAvailability) => {
          const items = get<EmployeeAvailability[]>('availability', INITIAL_AVAILABILITY);
          const exists = items.find(a => a.employeeId === avail.employeeId);
          if (exists) {
              set('availability', items.map(a => a.employeeId === avail.employeeId ? avail : a));
          } else {
              set('availability', [...items, avail]);
          }
      }
  },
  settings: {
      getProfile: (tenantId: string) => {
          const allProfiles = get<Record<string, BusinessProfile>>('businessProfiles', INITIAL_BUSINESS_PROFILES);
          return allProfiles[tenantId] || {
              name: 'My Cafe',
              address: '',
              phone: '',
              email: '',
              taxId: '',
              logoUrl: '',
              themeColor: 'latte',
              notificationEmails: []
          };
      },
      updateProfile: (tenantId: string, profile: BusinessProfile) => {
          const allProfiles = get<Record<string, BusinessProfile>>('businessProfiles', INITIAL_BUSINESS_PROFILES);
          allProfiles[tenantId] = profile;
          set('businessProfiles', allProfiles);
      }
  },
  users: {
    getAll: () => get<User[]>('users', INITIAL_USERS), // Super Admin needs to see all for login check
    getByTenant: (tenantId: string) => get<User[]>('users', INITIAL_USERS).filter(u => u.tenantId === tenantId),
    getAllByEmail: (email: string) => {
        const users = get<User[]>('users', INITIAL_USERS);
        return users.filter(u => u.email.toLowerCase() === email.trim().toLowerCase());
    },
    login: (email: string, tenantId?: string) => {
      const cleanEmail = email.trim().toLowerCase();
      const users = get<User[]>('users', INITIAL_USERS);
      
      // If user is superadmin (hardcoded check for safety in this hybrid auth model)
      if (cleanEmail === 'ibrainmart@gmail.com') {
          return {
              id: 'superadmin',
              tenantId: PLATFORM_ID,
              name: 'Platform Owner',
              email: 'ibrainmart@gmail.com',
              role: UserRole.SUPER_ADMIN,
              status: 'active',
              completedTrainingRecipeIds: []
          } as User;
      }

      // Otherwise, filter by email AND tenantId (if provided)
      if (tenantId) {
          return users.find(u => u.email.toLowerCase() === cleanEmail && u.tenantId === tenantId);
      } else {
          // If no tenant provided, return the first one found (legacy behavior)
          return users.find(u => u.email.toLowerCase() === cleanEmail);
      }
    },
    add: (user: User) => {
        const users = get<User[]>('users', INITIAL_USERS);
        // Avoid duplicates ONLY if they are in the same tenant
        const exists = users.find(u => 
            u.email.toLowerCase() === user.email.toLowerCase() && 
            u.tenantId === user.tenantId
        );
        
        if (!exists) {
            set('users', [...users, user]);
        }
    },
    update: (user: User) => {
        const users = get<User[]>('users', INITIAL_USERS);
        set('users', users.map(u => u.id === user.id ? user : u));
    },
    delete: (id: string) => {
        const users = get<User[]>('users', INITIAL_USERS);
        set('users', users.filter(u => u.id !== id));
    }
  }
};

// Reset local demo storage so new seeds apply on next fetch
export const resetDemoStorage = () => {
    const keys = [
        'tenants','units','categories','inventory','supplies','stockReports','recipes','training','routineDefinitions','routineAssignments',
        'shifts','requests','availability','scheduleGroups','users','businessProfiles'
    ];
    keys.forEach(k => localStorage.removeItem(k));
};
